/// <reference lib="dom"/>
/** @jsx hh */
/** @jsxFrag ChiFragment */
import { ChiFragment, hh } from "@chi/chi";
import { useMemo } from "@chi/chi/hooks";
import type {
  WbaxBufferProps,
  WbaxNode,
  WbaxOscProps,
  WbaxParam,
  WbaxPeriodicWave,
  WbaxSourceProps,
} from "@/types.tsx";
import { DistortionCurve, type WbaxNoiseType, type WbaxRng } from "@/noise.tsx";
import { useId, useNoiseBuffer, useTime, WbaxTimeContext } from "@/context.tsx";

export interface AtProps {
  t?: number;
  time?: number;
}
export const At = (props: AtProps, children: ChiElem) => {
  const parent = useTime();
  const value = props.time ?? parent + (props.t ?? 0);
  return <WbaxTimeContext.Provider value={value}>{children}</WbaxTimeContext.Provider>;
};

export interface RepeatProps {
  n: number;
  every: number;
}
export const Repeat = (props: RepeatProps, children: ChiElem) => (
  <>
    {Array.from(
      { length: Math.max(0, props.n) },
      (_, i) => <At key={i} t={i * props.every}>{children}</At>,
    )}
  </>
);

export function PhaseWave(phase: number): WbaxPeriodicWave {
  return { real: [0, Math.sin(phase)], imag: [0, Math.cos(phase)] };
}

export interface LfoProps extends WbaxSourceProps {
  id?: string;
  rate?: WbaxParam;
  depth?: WbaxParam;
  offset?: WbaxParam;
  phase?: number;
  type?: WbaxOscProps["type"];
}
export const Lfo = (props: LfoProps) => {
  const wave = useMemo(
    () => (props.phase === undefined ? undefined : PhaseWave(props.phase)),
    [props.phase],
  );
  const swing = (
    <gain id={props.offset === undefined ? props.id : undefined} gain={props.depth ?? 1}>
      <osc
        type={wave ? undefined : props.type ?? "sine"}
        wave={wave}
        frequency={props.rate ?? 1}
        start={props.start}
        stop={props.stop}
        onEnded={props.onEnded}
      />
    </gain>
  );
  if (props.offset === undefined) return swing;
  return (
    <mix id={props.id}>
      {swing}
      <constant offset={props.offset} start={props.start} stop={props.stop} />
    </mix>
  );
};

export interface NoiseProps extends Omit<WbaxBufferProps, "buffer"> {
  type?: WbaxNoiseType;
  duration?: number;
  channels?: number;
  rng?: WbaxRng;
}
export const Noise = (props: NoiseProps) => {
  const { type, duration, channels, rng, ...rest } = props;
  const buffer = useNoiseBuffer({ type, duration, channels, rng });
  return <buffer {...rest} buffer={buffer} />;
};

export interface EchoProps {
  time?: WbaxParam;
  feedback?: WbaxParam;
  wet?: WbaxParam;
  dry?: WbaxParam;
  maxDelay?: number;
}
export const Echo = (props: EchoProps, children: ChiElem) => {
  const id = useId("echo");
  return (
    <mix>
      <gain gain={props.dry ?? 1}>
        <mix id={`${id}.in`}>{children}</mix>
      </gain>
      <gain gain={props.wet ?? 0.5}>
        <delay id={`${id}.delay`} delayTime={props.time ?? 0.25} maxDelay={props.maxDelay ?? 2}>
          <use id={`${id}.in`} />
          <gain gain={props.feedback ?? 0.4}>
            <use id={`${id}.delay`} />
          </gain>
        </delay>
      </gain>
    </mix>
  );
};

export interface DistortionProps {
  amount?: number;
  oversample?: OverSampleType;
}
export const Distortion = (props: DistortionProps, children: ChiElem) => {
  const curve = useMemo(() => DistortionCurve(props.amount ?? 50), [props.amount]);
  return (
    <shaper curve={curve} oversample={props.oversample ?? "4x"}>
      {children}
    </shaper>
  );
};

export interface SplitProps {
  channels?: number;
  each: (channel: WbaxNode, index: number) => WbaxNode;
}
export const Split = (props: SplitProps, children: ChiElem) => {
  const id = useId("split");
  const channels = props.channels ?? 2;
  return (
    <merger channels={channels}>
      <defs>
        <splitter id={id} channels={channels}>{children}</splitter>
      </defs>
      {Array.from(
        { length: channels },
        (_, i) => <chain key={i} inlet={i}>{props.each(<use id={id} outlet={i} />, i)}</chain>,
      )}
    </merger>
  );
};

export interface ForkProps {
  branches: ((input: WbaxNode) => WbaxNode)[];
}
export const Fork = (props: ForkProps, children: ChiElem) => {
  const id = useId("fork");
  return (
    <mix>
      <defs>
        <mix id={id}>{children}</mix>
      </defs>
      {props.branches.map((branch) => branch(<use id={id} />))}
    </mix>
  );
};

export interface DryWetProps {
  dry?: WbaxParam;
  wet?: WbaxParam;
  effect: (input: WbaxNode) => WbaxNode;
}
export const DryWet = (props: DryWetProps, children: ChiElem) => (
  <Fork
    branches={[
      (input) => <gain gain={props.dry ?? 1}>{input}</gain>,
      (input) => <gain gain={props.wet ?? 1}>{props.effect(input)}</gain>,
    ]}
  >
    {children}
  </Fork>
);

export interface FeedbackProps {
  gain?: WbaxParam;
  delay?: WbaxParam;
  maxDelay?: number;
  loop: (input: WbaxNode) => WbaxNode;
}
export const Feedback = (props: FeedbackProps, children: ChiElem) => {
  const id = useId("feedback");
  const back = props.delay === undefined
    ? <use id={id} />
    : (
      <delay delayTime={props.delay} maxDelay={props.maxDelay ?? 1}>
        <use id={id} />
      </delay>
    );
  return (
    <mix id={id}>
      {props.loop(
        <>
          {children}
          <gain gain={props.gain ?? 1}>{back}</gain>
        </>,
      )}
    </mix>
  );
};
